import { useState } from 'react'
import type { RouteInfo, LineInfo } from '@/lib/api'

type Language = 'el' | 'en'

interface RouteHeaderProps {
  line: LineInfo
  routes: RouteInfo[]
  selectedRoute: RouteInfo | null
  onRouteSelect: (route: RouteInfo) => void
  lang: Language
}

export function getRouteDirection(route: RouteInfo, lang: Language): { from: string; to: string } {
  const desc = lang === 'en'
    ? (route.RouteDescrEng || route.RouteDescr)
    : route.RouteDescr

  const match = desc.match(/^(.+?)\s*[-–]\s*(.+?)(?:\s*\[|$)/)
  if (match) {
    return { from: match[1].trim(), to: match[2].trim() }
  }
  return { from: desc, to: '' }
}

function getRouteVariant(route: RouteInfo, lang: Language): string | null {
  const desc = lang === 'en'
    ? (route.RouteDescrEng || route.RouteDescr)
    : route.RouteDescr
  const bracketMatch = desc.match(/\[(.+?)\]/)
  if (bracketMatch) return bracketMatch[1]
  return null
}

interface DirectionGroup {
  routeType: string
  direction: { from: string; to: string }
  routes: RouteInfo[]
}

function groupRoutesByDirection(routes: RouteInfo[], lang: Language): DirectionGroup[] {
  const groups = new Map<string, DirectionGroup>()

  for (const route of routes) {
    const routeType = route.RouteType || '1'
    if (!groups.has(routeType)) {
      groups.set(routeType, {
        routeType,
        direction: getRouteDirection(route, lang),
        routes: []
      })
    }
    groups.get(routeType)!.routes.push(route)
  }

  return Array.from(groups.values()).sort((a, b) =>
    parseInt(a.routeType) - parseInt(b.routeType)
  )
}

export function RouteHeader({
  line,
  routes,
  selectedRoute,
  onRouteSelect,
  lang
}: RouteHeaderProps) {
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null)

  const directionGroups = groupRoutesByDirection(routes, lang)
  const selectedGroup = directionGroups.find(g =>
    g.routes.some(r => r.RouteCode === selectedRoute?.RouteCode)
  )

  const handleGroupClick = (group: DirectionGroup) => {
    if (group.routes.length === 1) {
      onRouteSelect(group.routes[0])
      setExpandedGroup(null)
    } else {
      if (expandedGroup === group.routeType) {
        setExpandedGroup(null)
      } else {
        setExpandedGroup(group.routeType)
        if (!group.routes.some(r => r.RouteCode === selectedRoute?.RouteCode)) {
          onRouteSelect(group.routes[0])
        }
      }
    }
  }

  const handleVariantSelect = (route: RouteInfo, groupType: string) => {
    onRouteSelect(route)
    setExpandedGroup(null)
  }

  return (
    <div className="route-header">
      <div className="route-line-badge">{line.LineID}</div>

      {directionGroups.length > 0 && (
        <div className="route-directions">
          {directionGroups.map((group) => {
            const isGroupSelected = selectedGroup?.routeType === group.routeType
            const isExpanded = expandedGroup === group.routeType
            const hasVariants = group.routes.length > 1

            return (
              <div key={group.routeType} className="direction-group">
                <button
                  className={`route-direction-btn ${isGroupSelected ? 'selected' : ''} ${hasVariants ? 'has-variants' : ''}`}
                  onClick={() => handleGroupClick(group)}
                >
                  <div className="direction-main">
                    <span className="direction-from">{group.direction.from}</span>
                    <span className="direction-arrow">➜</span>
                    <span className="direction-to">{group.direction.to}</span>
                  </div>
                  {hasVariants && (
                    <span className={`variant-indicator ${isExpanded ? 'expanded' : ''}`}>
                      {group.routes.length} routes ▾
                    </span>
                  )}
                </button>

                {isExpanded && hasVariants && (
                  <div className="variant-dropdown">
                    {group.routes.map((route) => {
                      const variant = getRouteVariant(route, lang)
                      const isRouteSelected = selectedRoute?.RouteCode === route.RouteCode
                      return (
                        <button
                          key={route.RouteCode}
                          className={`variant-btn ${isRouteSelected ? 'selected' : ''}`}
                          onClick={() => handleVariantSelect(route, group.routeType)}
                        >
                          {variant || 'Standard route'}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
